import Broadcasts from '../collections/Broadcasts';
import Broadcast from '../models/Broadcast';
import Contacts from '../collections/Contacts';
import { debounce } from 'lodash';
import { baseApi } from 'core/api';

const state = () => ({
    _item: null,
    _items: [],
    _pagination: null,
    _parameters: {
        page: 1,
        sortBy: null,
        sortOrder: null,
        sortType: null,
        search: null,
        length: 10,
        filter: null,
        filtersList: [],
        is_rvm: 0,
    },
    _contactsItems: null,
    _contactsPagination: null,
    _contactsParameters: {
        page: 1,
        sortBy: 'created_at',
        sortOrder: 'desc',
        search: null,
        length: 10,
        filter: null,
    },
    _broadcastsLoading: false,
    _broadcastsLoadingError: false,
    _broadcastAmountCredits: 0,
    _editing: null,
});

const getters = {
    broadcast: (state) => state._item,
    broadcasts: (state) => state._items,
    broadcastsPagination: (state) => state._pagination,
    broadcastsParameters: (state) => state._parameters,

    contactsBroadcast: (state) => state._contactsItems,
    contactsBroadcastPagination: (state) => state._contactsPagination,
    contactsBroadcastParameters: (state) => state._contactsParameters,
    broadcastsLoading: (state) => state._broadcastsLoading,
    broadcastsLoadingError: (state) => state._broadcastsLoadingError,
    broadcastAmountCredits: (state) => state._broadcastAmountCredits,
};

const utils = {
    broadcastsLoad: (storeInstance, retry) => {
        if (retry === true) {
            storeInstance._setBroadcastsRetryLoading();
        }

        storeInstance._setBroadcastsLoading(true);

        return baseApi
            .get('core/broadcasts', {
                params: storeInstance._parameters,
            })
            .then((response) => {
                storeInstance._setBroadcastsLoaded({
                    data: response.data,
                    rootGetters: storeInstance.rootGetters,
                });
                return response;
            })
            .catch(() => {
                storeInstance._setBroadcastsErrorLoading();
            })
            .finally(() => {
                storeInstance._setBroadcastsLoading(false);
            });
    },
};

const debounceContactsBroadcastLoad = debounce((storeInstance, action) => {
    storeInstance[action]();
}, 500);
const debounceBroadcastsLoad = debounce(utils.broadcastsLoad, 500);

const actions = {
    broadcastsLoad(retry) {
        this._setBroadcastsLoading(true);
        return debounceBroadcastsLoad(this, retry);
    },

    broadcastsClearSearch() {
        this._setBroadcastsSearchCleared();
        this.broadcastsLoad();
    },

    broadcastCreate(form) {
        return form.post('core/broadcasts').then((response) => {
            return response;
        });
    },

    broadcastDelete({ form, id }) {
        return form.delete(`core/broadcasts/${id}`).then(() => {
            this.broadcastsLoad();
        });
    },

    handleBroadcastAction({ id, status }) {
        return baseApi.put(`core/broadcasts/${id}/${status}`).then((response) => {
            this._setBroadcastUpdated(response.data);
            return new Broadcast(response.data);
        });
    },

    broadcastStartFinishDate(params) {
        return baseApi.get('core/broadcasts/schedule', { params }).then((response) => {
            return response;
        });
    },

    broadcastCreated(broadcast) {
        this._setBroadcastCreated(broadcast);
        this.broadcastsLoad();
    },

    broadcastEdit(form) {
        return form.put(`core/broadcasts/${form.id}`).then((response) => {
            this._setBroadcastUpdated(response.data);
            return response;
        });
    },

    broadcastClone(form) {
        return form.post('core/broadcasts').then((response) => {
            this._setBroadcastCreated(response.data);
            return response;
        });
    },

    broadcastTest(form) {
        return form.post('core/broadcasts/test').then((response) => {
            return response.data;
        });
    },

    broadcastFailed({ params, id }) {
        return baseApi.get(`core/broadcasts/failed/${id}`, { params }).then((response) => {
            return response.data;
        });
    },

    broadcastsUpdateSort(sortBy) {
        if (sortBy === null) {
            this._setBroadcastsUpdateSort({ sortBy: null, sortOrder: null, sortType: null });
            this.broadcastsLoad();
            return;
        }

        let sortOrder = 'asc';
        let sortType = null;

        if (this._parameters.sortBy === sortBy) {
            sortOrder = this._parameters.sortOrder === 'asc' ? 'desc' : 'asc';
        }

        if (sortBy === 'morphSentBy') {
            sortType = 'morph';
        }

        if (['total_count', 'success_count', 'reply_count'].includes(sortBy)) {
            sortType = 'daily_broadcast_statistics';
        }

        this._setBroadcastsUpdateSort({ sortBy, sortOrder, sortType });
        this.broadcastsLoad();
    },

    broadcastsUpdateSearch(search) {
        this._setBroadcastsUpdateSearch(search);
        this.broadcastsLoad();
    },

    broadcastsUpdateFilter(filter) {
        this._setBroadcastsUpdateFilter(filter);
        this.broadcastsLoad();
    },

    broadcastsUpdatePage(page) {
        this._setBroadcastsUpdatePage(page);
        this.broadcastsLoad();
    },

    broadcastsUpdateLength(length) {
        this._setBroadcastsUpdateLength(length);
        this.broadcastsLoad();
    },

    broadcastsCustomUpdateLengthSelector({ page, data }) {
        this._setBroadcastsUpdateLength(page);
        this.broadcastsUpdatePage(data);
    },

    broadcastsFilters() {
        const params = {
            withCustomFields: true,
        };

        return baseApi.get('core/contacts/filters', { params }).then((response) => {
            return response.data;
        });
    },

    broadcastsPageFilters() {
        const params = {
            withCustomFields: true,
        };

        return baseApi.get('core/broadcasts/filters', { params }).then((response) => {
            return response.data;
        });
    },

    async broadcastsFiltersApply(filters) {
        this._setBroadcastsUpdatePage(1);
        this._setBroadcastsFiltersApply(filters);
        return this.broadcastsLoad();
    },

    broadcastsResetParameters() {
        this._setBroadcastsReset();
        this._setBroadcastsResetParameters();
    },

    async broadcastsPageFiltersApply(filters) {
        this._setBroadcastsFiltersApply(filters);
        return this.broadcastsLoad();
    },

    getBroadcastById(id) {
        return baseApi.get(`core/broadcasts/${id}`).then((response) => {
            return new Broadcast(response.data);
        });
    },

    exportBroadcastById({ id, filter }) {
        const params = {
            filter: filter,
        };

        return baseApi.get(`core/broadcasts/contacts/${id}/export`, { params });
    },

    contactsBroadcastLoad(retry) {
        if (retry === true) {
            this._setContactsBroadcastRetryLoading();
        }

        return baseApi
            .get(`core/broadcasts/contacts/${this._item.id}`, {
                params: this._contactsParameters,
            })
            .then((response) => {
                this._setContactsBroadcastLoaded(response.data);
                return response;
            })
            .catch(() => {
                this._setContactsBroadcastErrorLoading();
            });
    },

    contactsBroadcastUpdatePage(page) {
        this._setContactsBroadcastUpdatePage(page);
        debounceContactsBroadcastLoad(this, 'contactsBroadcastLoad');
    },

    contactsBroadcastUpdateLength(length) {
        this._setContactsBroadcastUpdateLength(length);
        debounceContactsBroadcastLoad(this, 'contactsBroadcastLoad');
    },

    contactsBroadcastUpdateFilter({ filter, request = true }) {
        this._setContactsBroadcastUpdateFilter(filter);

        if (request) {
            debounceContactsBroadcastLoad(this, 'contactsBroadcastLoad');
        }
    },

    contactsBroadcastUpdateSearch(search) {
        this._setContactsBroadcastUpdateSearch(search);
        debounceContactsBroadcastLoad(this, 'contactsBroadcastLoad');
    },

    contactsBroadcastClearSearch() {
        this._setContactsBroadcastSearchCleared();
        debounceContactsBroadcastLoad(this, 'contactsBroadcastLoad');
    },

    broadcastChoose(broadcast) {
        this._setBroadcastChoose(broadcast);
    },

    changeBroadcastAmountCredits(amount) {
        this._setBroadcastsAmountCredits(amount);
    },

    broadcastsStateUpdate(data) {
        this._setBroadcastsStateUpdate(data);
    },

    ringlessBroadcastsUpdateParameters(type) {
        this._setRinglessBroadcastsUpdateParameters(type);
    },

    broadcastsEstimateCount(data) {
        return baseApi;
        ('core/broadcasts/count-estimated-credits', data.then((response) => response.data));
    },

    // Private methods for all operations
    _setBroadcastsStateUpdate(data) {
        for (let key in data) {
            const mappedKey = key.startsWith('_') ? key : `_${key}`;
            this[mappedKey] = data[key];
        }
    },

    _setBroadcastChoose(broadcast) {
        this._item = broadcast;
    },

    _setBroadcastsFiltersApply(filtersList) {
        this._parameters.filtersList = filtersList;
    },

    _setBroadcastCreated(data) {
        const item = new Broadcast(data);

        if (this._items === null) {
            return;
        }

        this._items.unshift(item);

        if (this._pagination) {
            ++this._pagination.total;
        }
    },

    _setBroadcastUpdated(data) {
        const item = new Broadcast(data);

        if (!this._items.length) return;

        this._items = this._items.map((o) => {
            if (o.id === item.id) {
                return item;
            } else {
                return o;
            }
        });
    },

    _setBroadcastsReset() {
        this._items = [];
        this._broadcastsLoadingError = false;
        this._broadcastsLoading = false;
        this._pagination = null;
    },

    _setBroadcastsResetParameters() {
        this._parameters = {
            page: 1,
            sortBy: null,
            sortOrder: null,
            search: null,
            length: 10,
            filter: null,
            filtersList: [],
            is_rvm: 0,
        };
    },

    _setRinglessBroadcastsUpdateParameters(type) {
        this._parameters = {
            is_rvm: type,
        };
    },

    _setBroadcastsLoaded({ data, rootGetters }) {
        const items = new Broadcasts(
            data.data,
            {},
            {
                members: rootGetters.members,
            }
        );

        this._items = items.models;
        this._pagination = {
            current_page: data.meta.current_page,
            from: data.meta.from,
            last_page: data.meta.last_page,
            per_page: data.meta.per_page,
            to: data.meta.to,
            total: data.meta.total,
        };
    },

    _setBroadcastsRetryLoading() {
        this._items = [];
        this._broadcastsLoading = true;
        this._broadcastsLoadingError = false;
        if (this._pagination) {
            this._pagination.total = 0;
        }
    },

    _setBroadcastsLoading(status) {
        this._broadcastsLoading = status;
    },

    _setBroadcastsErrorLoading() {
        this._items = [];
        this._broadcastsLoadingError = true;
        if (this._pagination) {
            this._pagination.total = 0;
        }
    },

    _setBroadcastEditing(data) {
        this._editing = data;
    },

    _setBroadcastsUpdateSort(data) {
        this._parameters.sortBy = data.sortBy;
        this._parameters.sortOrder = data.sortOrder;
        this._parameters.sortType = data.sortType;
    },

    _setBroadcastsUpdateSearch(data) {
        this._parameters.page = 1;
        this._parameters.search = data;
    },

    _setBroadcastsUpdateFilter(data) {
        this._parameters.filter = data;
    },

    _setBroadcastsUpdatePage(data) {
        this._parameters.page = data;
    },

    _setBroadcastsUpdateLength(data) {
        this._parameters.length = data;
    },

    _setBroadcastsSearchCleared() {
        this._parameters.search = null;
    },

    _setContactsBroadcastLoaded(data) {
        const contacts = new Contacts(data.data);

        this._contactsItems = contacts.models;
        this._contactsPagination = {
            current_page: data.current_page,
            from: data.from,
            last_page: data.last_page,
            per_page: data.per_page,
            to: data.to,
            total: data.total,
        };
    },

    _setContactsBroadcastRetryLoading() {
        this._contactsItems = null;
        if (this._contactsPagination) {
            this._contactsPagination.total = 0;
        }
    },

    _setContactsBroadcastErrorLoading() {
        this._contactsItems = false;
        if (this._contactsPagination) {
            this._contactsPagination.total = 0;
        }
    },

    _setContactsBroadcastUpdatePage(data) {
        this._contactsParameters.page = data;
    },

    _setContactsBroadcastUpdateLength(data) {
        this._contactsParameters.length = data;
    },

    _setContactsBroadcastUpdateFilter(data) {
        this._contactsParameters.filter = data;
    },

    _setContactsBroadcastUpdateSearch(data) {
        this._contactsParameters.search = data;
    },

    _setContactsBroadcastSearchCleared() {
        this._contactsParameters.search = null;
    },

    _setBroadcastsAmountCredits(data) {
        this._broadcastAmountCredits = data;
    }
};

export default {
    state,
    getters,
    actions
};
